import { AvailableLanguages, ITranslationOverrides } from "@smartrr/shared/entities/CustomerPortalTheme";
import { NestedTranslationOverrides, smartrri18next } from "@smartrr/shared/utils/sharedTranslations/localize";
import { localizeDefault } from "@smartrr/shared/utils/sharedTranslations/localizeDefault";
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import moment from "moment";
import { useMemo, useState } from "react";

// To be called on account portal and passwordless init
async function getOverwrittenCopy(
  translationOverrides: ITranslationOverrides | undefined
): Promise<NestedTranslationOverrides<string>> {
  if (!translationOverrides) {
    return localizeDefault;
  }
  const updatedTranslationList = cloneDeep(localizeDefault);
  const activeLanguage = translationOverrides.activeLanguage ?? AvailableLanguages.ENGLISH;

  await smartrri18next.changeLanguage(activeLanguage);

  for (const key of Object.keys(translationOverrides[activeLanguage])) {
    /**
     * Keys are formatted as "[LANG]__[PATH]"
     * ie: "en__general.items" OR "fr__subscriptions.confirm"
     */
    const unFormattedKey = key.split("__").pop();
    // If enabled in admin portal, override text
    if (translationOverrides[activeLanguage][key].enabled) {
      set(updatedTranslationList, unFormattedKey!, translationOverrides[activeLanguage][key].value);
    }
  }

  return updatedTranslationList;
}

export function useI18NextResourceBundle(translationOverrides: ITranslationOverrides | undefined) {
  const [, setTranslationUpdated] = useState(false);

  useMemo(() => {
    setI18NextResourceBundle(translationOverrides).then(() => {
      /** Inform ThemeProvider that the theme has changed since translation text has changed. */
      setTranslationUpdated(true);
    });
  }, [translationOverrides]);
}

export async function setI18NextResourceBundle(translationOverrides: ITranslationOverrides | undefined) {
  const activeLanguage = translationOverrides?.activeLanguage ?? AvailableLanguages.ENGLISH;
  moment.locale(activeLanguage);

  const overrides = await getOverwrittenCopy(translationOverrides);

  if (!overrides) {
    return;
  }

  smartrri18next.removeResourceBundle(activeLanguage, "translation");
  smartrri18next.addResourceBundle(activeLanguage, "translation", overrides, true, true);

  /** The type in i18n is { [key]: { [key]: string }} which is not useful. */
  const translation: typeof localizeDefault | null = (smartrri18next.getDataByLanguage(activeLanguage)
    ?.translation ?? null) as typeof localizeDefault | null;

  if (translation) {
    moment.updateLocale(activeLanguage, {
      months: [
        translation.defaults.dates.months.January,
        translation.defaults.dates.months.February,
        translation.defaults.dates.months.March,
        translation.defaults.dates.months.April,
        translation.defaults.dates.months.May,
        translation.defaults.dates.months.June,
        translation.defaults.dates.months.July,
        translation.defaults.dates.months.August,
        translation.defaults.dates.months.September,
        translation.defaults.dates.months.October,
        translation.defaults.dates.months.November,
        translation.defaults.dates.months.December,
      ],
      weekdays: [
        translation.defaults.dates.weekdays.Sunday,
        translation.defaults.dates.weekdays.Monday,
        translation.defaults.dates.weekdays.Tuesday,
        translation.defaults.dates.weekdays.Wednesday,
        translation.defaults.dates.weekdays.Thursday,
        translation.defaults.dates.weekdays.Friday,
        translation.defaults.dates.weekdays.Saturday,
      ],
    });
  }
}
